import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';

/**
 * Obchodní podmínky a Novinky.
 *
 * Text VOP je centrální – do instance ho zapisuje superadmin panel, tady se
 * jen čte. Souhlas uděluje majitel (role ADMIN); dokud ho neudělí, vrací API
 * na ostatní cesty 451 a ve webu běží blokující úvodní okno.
 */

export interface TermsAcceptance {
  acceptedAt: string;
  userName:   string;
  userEmail:  string;
  version:    string;
}

export interface TermsData {
  available:      boolean;
  version?:       string;
  effectiveDate?: string | null;
  title?:         string;
  body?:          string;
  /** Uvítací text v úvodním okně – volitelný, edituje se v superadminu. */
  intro?:         string | null;
  consentText?:   string;
  supportEmail?:  string | null;
  supportPhone?:  string | null;
  /** Firma ještě neodsouhlasila aktuální verzi. */
  pending?:       boolean;
  /** Tenhle uživatel musí odsouhlasit hned (blokující okno). */
  required?:      boolean;
  canAccept?:     boolean;
  support?:       boolean;
  acceptance?:    TermsAcceptance | null;
}

export function useTerms() {
  return useQuery<TermsData>({
    queryKey: ['terms'],
    queryFn:  async () => (await apiClient.get('/terms')).data.data,
    // Krátce: po publikaci nové verze začne API blokovat hned a web by jinak
    // držel starý stav „odsouhlaseno" a jen sypal chyby.
    staleTime: 30 * 1000,
    retry: 1,
  });
}

export function useAcceptTerms() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (version?: string) =>
      (await apiClient.post('/terms/accept', { version })).data.data,
    onSuccess: () => {
      // Po souhlasu se odblokuje zbytek API – zahodíme celou cache,
      // aby se dotazy, které předtím spadly na 451, načetly znovu.
      qc.invalidateQueries();
    },
  });
}

export interface ChangelogEntry {
  id:        string;
  date:      string;
  category:  string;
  title:     string;
  body:      string;
  highlight:     boolean;
  highlightDays: number;
}

/**
 * Oznámení na nástěnku – jen zvýrazněné novinky, které jsou ještě v okně
 * svých dnů. Server je odfiltruje sám, tady se jen zobrazují.
 */
export function useChangelogHighlights() {
  return useQuery<ChangelogEntry[]>({
    queryKey: ['changelog-highlights'],
    queryFn:  async () => (await apiClient.get('/changelog/highlights')).data.data,
    // Krátce, aby odpublikované nebo vypršelé oznámení zmizelo i uživateli,
    // který má dashboard otevřený celý den.
    staleTime: 3 * 60 * 1000,
    refetchOnWindowFocus: true,
    retry: false,
  });
}

export function useChangelog() {
  return useQuery<ChangelogEntry[]>({
    queryKey: ['changelog'],
    queryFn:  async () => (await apiClient.get('/changelog')).data.data,
    staleTime: 30 * 60 * 1000,
    retry: false,
  });
}

/** Vykreslí velmi jednoduchý markdown (nadpisy, tučné, odrážky) na HTML. */
export function renderTermsHtml(md: string): string {
  const esc = (t: string) => t
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const inline = (t: string) => esc(t)
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>');

  const out: string[] = [];
  let listOpen = false;
  const closeList = () => { if (listOpen) { out.push('</ul>'); listOpen = false; } };

  for (const raw of md.split('\n')) {
    const line = raw.trim();
    if (!line)                      { closeList(); continue; }
    if (line.startsWith('### '))    { closeList(); out.push(`<h4>${inline(line.slice(4))}</h4>`); continue; }
    if (line.startsWith('## '))     { closeList(); out.push(`<h3>${inline(line.slice(3))}</h3>`); continue; }
    if (line.startsWith('# '))      { closeList(); out.push(`<h2>${inline(line.slice(2))}</h2>`); continue; }
    if (/^[-*]\s+/.test(line) || /^[a-d]\)\s+/.test(line)) {
      if (!listOpen) { out.push('<ul>'); listOpen = true; }
      out.push(`<li>${inline(line.replace(/^[-*]\s+/, ''))}</li>`);
      continue;
    }
    closeList();
    out.push(`<p>${inline(line)}</p>`);
  }
  closeList();
  return out.join('\n');
}
